import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { RequestModal } from '../../components/trips/RequestModal';
import { SendOfferModal } from '../../components/trips/SendOfferModal';
import { useAppDispatch } from '../../store/hooks';
import { openOrCreateConversationByUserId } from '../../store/chatThunks';
import {
  CalendarDays,
  MapPin,
  Package,
  Truck,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Star,
  Shield,
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';

const formatRoute = (trip, t) => {
  const originCity = trip.originCity || trip.originDetails?.city;
  const originCountry = trip.originCountry || trip.originDetails?.country;
  const destinationCity = trip.destinationCity || trip.destinationDetails?.city;
  const destinationCountry =
    trip.destinationCountry || trip.destinationDetails?.country;

  return `${originCity || t('home.from')}, ${originCountry || ''} → ${
    destinationCity || t('home.to')
  }, ${destinationCountry || ''}`;
};

const formatDeparture = (trip, t) => {
  if (!trip.departureDate) return t('tripCard.scheduleTbd');
  if (trip.departureTime) {
    return `${trip.departureDate} • ${trip.departureTime}`;
  }
  return trip.departureDate;
};

const formatCapacity = trip => {
  if (!trip.availableSpace) return '—';
  return `${trip.availableSpace} ${trip.spaceUnit || 'kg'}`;
};

const formatPrice = (trip, t) => {
  const currency = trip.currency || 'USD';
  if (trip.pricingMode === 'perKg' && trip.pricePerKg) {
    return `${currency} ${trip.pricePerKg}/${trip.spaceUnit || 'kg'}`;
  }
  if (trip.pricingMode === 'perUnit' && trip.pricePerUnit) {
    return `${currency} ${trip.pricePerUnit}/${trip.spaceUnit || 'unit'}`;
  }
  if (trip.pricingMode === 'flat' && trip.flatPrice) {
    return `${currency} ${trip.flatPrice} flat`;
  }
  if (trip.flatPrice) {
    return `${currency} ${trip.flatPrice}`;
  }
  return t('tripCard.contactForPrice');
};

export default function TripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = usePublicAuth();
  const { t } = useTranslation();
  const [trip, setTrip] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [completedCounts, setCompletedCounts] = useState({
    senderCompleted: 0,
    travelerCompleted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);

  const userId = useMemo(
    () =>
      trip?.createdBy || trip?.ownerId || trip?.createdByUid || trip?.userId,
    [trip],
  );

  useEffect(() => {
    const loadTripData = async () => {
      if (!tripId) {
        setError(t('tripDetail.tripIdNotFound'));
        setLoading(false);
        return;
      }

      try {
        // Fetch trip
        const tripDoc = await getDoc(doc(db, 'trips', tripId));
        if (!tripDoc.exists()) {
          setError(t('tripDetail.tripNotFound'));
          setLoading(false);
          return;
        }

        const tripData = { id: tripDoc.id, ...tripDoc.data() };
        setTrip(tripData);

        const ownerId =
          tripData.createdBy ||
          tripData.ownerId ||
          tripData.createdByUid ||
          tripData.userId;

        if (ownerId) {
          // Fetch owner profile
          const userDoc = await getDoc(doc(db, 'users', ownerId));
          if (userDoc.exists()) {
            setOwnerProfile(userDoc.data());
          }

          // Fetch completed requests
          const [requesterSnap, tripOwnerSnap] = await Promise.all([
            getDocs(
              query(
                collection(db, 'requests'),
                where('requesterId', '==', ownerId),
              ),
            ),
            getDocs(
              query(
                collection(db, 'requests'),
                where('tripOwnerId', '==', ownerId),
              ),
            ),
          ]);

          let senderCompleted = 0;
          let travelerCompleted = 0;

          requesterSnap.docs.forEach(doc => {
            const requestData = doc.data();
            if ((requestData?.status || '').toLowerCase() !== 'completed')
              return;

            const tripType = requestData.tripType;
            if (tripType === 'shipping') {
              senderCompleted++;
            } else if (tripType === 'carrying') {
              travelerCompleted++;
            }
          });

          tripOwnerSnap.docs.forEach(doc => {
            const requestData = doc.data();
            if ((requestData?.status || '').toLowerCase() !== 'completed')
              return;

            const tripType = requestData.tripType;
            if (tripType === 'carrying') {
              senderCompleted++;
            } else if (tripType === 'shipping') {
              travelerCompleted++;
            }
          });

          setCompletedCounts({
            senderCompleted,
            travelerCompleted,
          });
        }
      } catch (err) {
        console.error('Error loading trip:', err);
        setError(t('tripDetail.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadTripData();
  }, [tripId]);

  const initials =
    trip?.createdByName?.slice(0, 1).toUpperCase() ||
    trip?.createdByEmail?.slice(0, 1).toUpperCase() ||
    'F';

  const isVerified = ownerProfile?.verificationStatus === 'verified';
  const rating = ownerProfile?.averageRating || ownerProfile?.rating || 4.5;
  const reviewCount =
    ownerProfile?.totalReviews || ownerProfile?.reviewsCount || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-yellow-500 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-slate-400">
                {t('tripDetail.loading')}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center">
            <AlertCircle className="h-12 w-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {error || t('tripDetail.tripNotFound')}
            </h2>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('tripDetail.notFoundDescription')}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-slate-950 dark:to-slate-900 mt-20">
      <MainNavbar />
      <main className="mx-auto max-w-6xl px-4 pt-24 pb-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar - Left */}
          <div className="space-y-6">
            {/* User Profile Card */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 p-8 shadow-2xl border-2 border-gray-200 dark:border-slate-800">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="flex h-24 w-24 items-center justify-center rounded-[50%] bg-black dark:bg-slate-700 text-3xl font-black text-yellow-400 shadow-lg">
                    {initials}
                  </div>
                  {isVerified && (
                    <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 border-2 border-white dark:border-slate-900">
                      <CheckCircle size={14} className="text-white" />
                    </div>
                  )}
                </div>
                <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-3">
                  {trip.createdByName || t('tripCard.flexyUser')}
                </h1>
                {!isVerified && (
                  <div className="inline-flex items-center gap-1.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-3 py-1 rounded-full text-sm font-bold mb-4">
                    <AlertCircle size={12} />
                    {t('tripDetail.unverified')}
                  </div>
                )}
                <div className="flex flex-col items-center gap-3 text-base mb-5">
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold text-gray-900 dark:text-white text-lg">
                      {rating.toFixed(1)}
                    </span>
                    {reviewCount > 0 && (
                      <span className="text-gray-500 dark:text-slate-400 text-sm">
                        {t('tripDetail.reviews', { count: reviewCount })}
                      </span>
                    )}
                  </div>
                  {(completedCounts.senderCompleted > 0 ||
                    completedCounts.travelerCompleted > 0) && (
                    <div className="text-gray-500 dark:text-slate-400 text-sm font-semibold">
                      {t('tripDetail.completedTrips', {
                        count:
                          completedCounts.senderCompleted +
                          completedCounts.travelerCompleted,
                      })}
                    </div>
                  )}
                </div>
                {userId && (
                  <Link
                    to={`/profile/detail/${userId}`}
                    className="w-full rounded-xl bg-black dark:bg-slate-700 px-6 py-3 text-base font-bold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800 shadow-md mb-6"
                  >
                    {t('tripDetail.viewProfile')}
                  </Link>
                )}

                {/* Completed Jobs */}
                {(completedCounts.senderCompleted > 0 ||
                  completedCounts.travelerCompleted > 0) && (
                  <div className="w-full space-y-3 mb-6">
                    {completedCounts.senderCompleted > 0 && (
                      <div className="rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-5 text-center">
                        <div className="text-4xl font-black text-green-600 dark:text-green-400 mb-2">
                          {completedCounts.senderCompleted}
                        </div>
                        <div className="text-sm font-bold text-green-800 dark:text-green-300">
                          {t('tripDetail.senderDeliveries')}
                        </div>
                      </div>
                    )}
                    {completedCounts.travelerCompleted > 0 && (
                      <div className="rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-5 text-center">
                        <div className="text-4xl font-black text-green-600 dark:text-green-400 mb-2">
                          {completedCounts.travelerCompleted}
                        </div>
                        <div className="text-sm font-bold text-green-800 dark:text-green-300">
                          {t('tripDetail.travelerDeliveries')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content - Right */}
          <div className="lg:col-span-2 space-y-6">
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                className="flex-1 rounded-xl bg-black dark:bg-slate-700 px-6 py-4 text-center text-base font-bold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800 shadow-lg"
                onClick={() => {
                  if (!user) {
                    navigate('/auth/login');
                    return;
                  }

                  const tripType = trip?.tripType || 'shipping';

                  if (tripType === 'shipping') {
                    // Sender requesting from traveler's trip
                    setShowRequestModal(true);
                  } else if (tripType === 'carrying') {
                    // Traveler offering to sender's request
                    setShowOfferModal(true);
                  }
                }}
                >
                {trip?.tripType === 'carrying'
                  ? t('tripDetail.sendOffer')
                  : t('tripDetail.requestDelivery')}
              </button>
              <button
                type="button"
                className="flex-1 rounded-xl border-2 border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-6 py-4 text-center text-base font-bold text-gray-900 dark:text-white transition hover:border-gray-400 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800"
                onClick={async () => {
                  if (!user) {
                    navigate('/auth/login');
                    return;
                  }

                  if (userId) {
                    try {
                      // Open or create conversation with the trip owner
                      const conversation = await dispatch(
                        openOrCreateConversationByUserId(
                          userId,
                          trip?.createdByName || 'User',
                        ),
                      );

                      // Navigate to chat with conversation ID
                      navigate(`/chat/${conversation.id}`);
                    } catch (error) {
                      console.error('Error opening conversation:', error);
                      // Fallback to chat page
                      navigate('/chat');
                    }
                  } else {
                    navigate('/chat');
                  }
                }}
              >
                <MessageCircle className="inline-block h-5 w-5 mr-2" />
                {t('myRequests.message')}
              </button>
            </div>

            {/* Trip Information */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-lg border border-gray-100 dark:border-slate-800">
              <h2 className="text-xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-yellow-500" />
                {t('tripDetail.tripInformation')}
              </h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-slate-800">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black dark:bg-slate-700 text-white flex-shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                      {t('tripDetail.route')}
                    </div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatRoute(trip)}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-slate-800">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black dark:bg-slate-700 text-white flex-shrink-0">
                    <CalendarDays className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                      {t('tripDetail.departure')}
                    </div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatDeparture(trip)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trip Details */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-lg border border-gray-100 dark:border-slate-800">
              <h2 className="text-xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <Package className="h-5 w-5 text-yellow-500" />
                {t('tripDetail.tripDetails')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {trip.tripType && (
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-slate-800">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                      {t('tripDetail.tripType')}
                    </div>
                    <div className="text-base font-bold text-gray-900 dark:text-white">
                      {trip.tripType === 'carrying'
                        ? t('tripDetail.lookingForSpace')
                        : t('tripDetail.offeringShipping')}
                    </div>
                  </div>
                )}
                {trip.materialType && trip.tripType === 'carrying' && (
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-slate-800">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                      {t('tripDetail.materialType')}
                    </div>
                    <div className="text-base font-bold text-gray-900 dark:text-white capitalize">
                      {trip.materialType}
                    </div>
                  </div>
                )}
                {trip.deliveryMethod && trip.tripType === 'shipping' && (
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-slate-800">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                      {t('tripDetail.deliveryMethod')}
                    </div>
                    <div className="text-base font-bold text-gray-900 dark:text-white">
                      {trip.deliveryMethod === 'byRail'
                        ? t('travelerTripRequests.byRail')
                        : trip.deliveryMethod === 'byAir'
                        ? t('travelerTripRequests.byAir')
                        : t('travelerTripRequests.byRoad')}
                    </div>
                  </div>
                )}
                {trip.availableSpace && (
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-slate-800">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                      {trip.tripType === 'carrying'
                        ? t('tripDetail.spaceNeeded')
                        : t('tripDetail.capacityAvailable')}
                    </div>
                    <div className="text-base font-bold text-gray-900 dark:text-white">
                      {formatCapacity(trip)}
                    </div>
                  </div>
                )}
                {(trip.pricePerKg || trip.flatPrice || trip.pricePerUnit) && (
                  <div className="p-4 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800">
                    <div className="text-xs font-bold uppercase tracking-wider text-yellow-700 dark:text-yellow-400 mb-2">
                      {t('tripCard.price')}
                    </div>
                    <div className="text-lg font-black text-yellow-900 dark:text-yellow-300">
                      {formatPrice(trip, t)}
                    </div>
                  </div>
                )}
              </div>
              {trip.itemDescription && (
                <div className="mt-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-700 dark:text-blue-400 mb-2">
                    {t('tripDetail.itemDescription')}
                  </div>
                  <div className="text-sm text-blue-900 dark:text-blue-300 leading-relaxed">
                    {trip.itemDescription}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            {trip.notes && (
              <div className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-lg border border-gray-100 dark:border-slate-800">
                <h2 className="text-xl font-black text-gray-900 dark:text-white mb-4">
                  {t('tripDetail.notesAndRules')}
                </h2>
                <div className="rounded-xl bg-gray-50 dark:bg-slate-800 p-5 border border-gray-200 dark:border-slate-700">
                  <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
                    {trip.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Security Note */}
            <div className="rounded-2xl bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-2 border-yellow-200 dark:border-yellow-800 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-yellow-900 dark:text-yellow-300 mb-1">
                    {t('tripDetail.securePaymentProtection')}
                  </div>
                  <p className="text-sm text-yellow-800 dark:text-yellow-400 leading-relaxed">
                    {t('tripDetail.securePaymentText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {trip && (
        <>
          <RequestModal
            visible={showRequestModal}
            onClose={() => setShowRequestModal(false)}
            trip={trip}
          />
          <SendOfferModal
            visible={showOfferModal}
            onClose={() => setShowOfferModal(false)}
            trip={trip}
          />
        </>
      )}
    </div>
  );
}
